/**
 * Maps an insight/pattern payload to the React view that renders its expanded
 * drill-down. Types without a bespoke entry fall back to GenericInsightBody.
 */
import type { ReactNode } from "react";

import type { Rec } from "./formatInsight";
import { AgentActivityTrendView } from "./views/AgentActivityTrendView";
import { AgentBookingSummaryView } from "./views/AgentBookingSummaryView";
import { AgentDataQualityView } from "./views/AgentDataQualityView";
import { CaptureRateView } from "./views/CaptureRateView";
import { DailyBookingSummaryView } from "./views/DailyBookingSummaryView";
import { DisruptionView } from "./views/DisruptionView";
import { EarlyBookingView } from "./views/EarlyBookingView";
import { GenericInsightBody } from "./views/GenericInsightBody";
import { LaneTrendView } from "./views/LaneTrendView";
import { NewlyOpenedView } from "./views/NewlyOpenedView";
import { PortfolioFunnelView } from "./views/PortfolioFunnelView";
import { PricingSourceTrendView } from "./views/PricingSourceTrendView";
import { OcfReconciliationAlertView } from "./views/OcfReconciliationAlertView";
import { ReportAlertView } from "./views/ReportAlertView";
import { SalesPersonTrendView } from "./views/SalesPersonTrendView";
import { VolumeTrendView } from "./views/VolumeTrendView";
import { WeeklyTrendView } from "./views/WeeklyTrendView";

type InsightRenderer = (payload: never) => ReactNode;

const INSIGHT_RENDERERS: Record<string, InsightRenderer> = {
  portfolio_opportunity_funnel: (p) => <PortfolioFunnelView payload={p} />,
  opportunity_capture_rate: (p) => <CaptureRateView payload={p} />,
  newly_opened_bookings: (p) => <NewlyOpenedView payload={p} />,
  daily_booking_summary: (p) => <DailyBookingSummaryView payload={p} />,
  weekly_trend: (p) => <WeeklyTrendView payload={p} />,
  agent_booking_summary: (p) => <AgentBookingSummaryView payload={p} />,
};

const PATTERN_RENDERERS: Record<string, InsightRenderer> = {
  customer_volume_trend: (p) => <VolumeTrendView payload={p} />,
  early_booking_market_decline: (p) => <EarlyBookingView payload={p} />,
  sales_person_activity_trend: (p) => <SalesPersonTrendView payload={p} />,
  rolled_delayed_disruption: (p) => <DisruptionView payload={p} />,
  pricing_match_by_source_type: (p) => <PricingSourceTrendView payload={p} />,
  lane_volume_and_rate_trend: (p) => <LaneTrendView payload={p} />,
  agent_activity_trend: (p) => <AgentActivityTrendView payload={p} />,
  agent_data_quality: (p) => <AgentDataQualityView payload={p} />,
};

/**
 * Resolve the renderer for a payload, defaulting to the generic view.
 *
 * `eventId` is only required by drill-downs that lazily fetch detail from a
 * feed-scoped endpoint (currently the OCF reconciliation alert).
 */
export function renderInsightBody(payload: Rec, eventId?: string): ReactNode {
  const generic = (p: Rec) => <GenericInsightBody payload={p} />;

  if (payload.kind === "insight" && typeof payload.insight_type === "string") {
    const renderer = INSIGHT_RENDERERS[payload.insight_type];
    return renderer ? renderer(payload as never) : generic(payload);
  }
  if (payload.kind === "pattern" && typeof payload.pattern_type === "string") {
    const renderer = PATTERN_RENDERERS[payload.pattern_type];
    return renderer ? renderer(payload as never) : generic(payload);
  }
  if (payload.kind === "alert") {
    if (payload.alert_type === "ocf_reconciliation_mismatch" && eventId) {
      return (
        <OcfReconciliationAlertView
          eventId={eventId}
          payload={payload as never}
        />
      );
    }
    return <ReportAlertView payload={payload as never} />;
  }
  return generic(payload);
}
